import React from 'react'

import Layout from '../components/layout'
import CardGrid from '../components/cardGrid'
import Slalom from '../components/slalom'
import ContentBlock from '../components/contentBlock'
import SEO from '../components/seo'

const topCards = [
  <>
    <div className='cardContent'>
      <h4>Intuitive to Developers</h4>
      <p>Realm’s object-oriented data model is simple to learn, doesn’t need an ORM, and lets you write less code.</p>
    </div>
  </>,
  <>
    <div className='cardContent'>
      <h4>Designed for Offline Use</h4>
      <p>Realm’s local database persists data on-disk, so apps work as well offline as they do online.</p>
    </div>
  </>,
  <>
    <div className='cardContent'>
      <h4>Built for Mobile</h4>
      <p>Realm is fully-featured, lightweight, and efficiently uses memory, disk space, and battery life.</p>
    </div>
  </>
]

const middleCards = [
  <>
    <h3>Fast Querying</h3>
    <p>Lazy loading and a zero-copy architecture mean complex queries complete in microseconds.</p>
  </>,
  <>
    <h3>Cross-Platform</h3>
    <p>Use a single database to build all your apps, on any major platform. Mobile, desktop and IoT.</p>
  </>,
  <>
    <h3>Open Source</h3>
    <p>Realm is distributed under the Apache 2.0 license and backed by MongoDB.</p>
  </>,
  <>
    <h3>Data Sync</h3>
    <p>The <a href='https://www.mongodb.com/realm/mobile/sync'>MongoDB Realm</a> Sync service makes it simple to keep data in sync across users, devices, and your backend in real-time.</p>
  </>
]

const slaloms = [
  {
    content: (
      <>
        <h3>Object-Oriented:<br />Streamline Your Code</h3>
        <p>Realm was built for mobile developers, with simplicity in mind. The idiomatic, object-oriented data model can save you thousands of lines of code.</p>
        <a href='https://docs.mongodb.com/realm/sdk'>Learn More  →</a>
      </>
    ),
    codeLanguages: [
      {
        name: 'Kotlin',
        snippets: [
          {
            code: '// Your object classes are your database schema definition\nopen class Dog(\n    var name: String = "",\n    var age: Int = 0\n): RealmObject()\n\nopen class Person(\n    @PrimaryKey var _id: String = "",\n    var name: String = "",\n    var age: Int = 0,\n    // Create relationships by pointing an Object field to another Class\n    var dogs: RealmList&#60;Dog&#62; = RealmList()\n): RealmObject()\n\n// Use Realm objects like regular Kotlin objects\nval dog = Dog()\ndog.name = "Rex"\ndog.age = 1\n\n// Initialize Realm (just once per application)\nRealm.init(context)\n\n// Get a Realm instance for this thread\nrealm = Realm.getDefaultInstance()\n\n// Add the dog to the realm\nrealm.executeTransaction {\n    it.copyToRealm(dog)\n}',
            position: '1'
          }
        ]
      }, {
        name: 'Swift',
        snippets: [
          {
            code: '// Define your models like regular Swift classes\nclass Dog: Object {\n    @objc dynamic var name = ""\n    @objc dynamic var age = 0\n}\nclass Person: Object {\n    @objc dynamic var _id = ""\n    @objc dynamic var name = ""\n    @objc dynamic var age = 0\n    // Create relationships by pointing an Object field to another Class\n    let dogs = List&#60;Dog&#62;()\n    \n    override static func primaryKey() -> String? {\n        return "_id"\n    }\n}\n// Use them like regular Swift objects\nlet dog = Dog()\ndog.name = "Rex"\ndog.age = 1\nprint("name of dog: \\(dog.name)")\n\n// Get the default Realm\nlet realm = try! Realm()\n// Persist your data easily with a write transaction \ntry! realm.write {\n    realm.add(dog)\n}',
            position: '1'
          }
        ]
      }, {
        name: 'JavaScript',
        snippets: [
          {
            code: '// Your object classes are your database schema definition\nconst Dog = {\n  name: "Dog",\n  properties: {\n    name: "string",\n    age: "int",\n    breed: "string?"\n  }\n};\n\nconst Person = {\n  name: "Person",\n  primaryKey: \'_id\',\n  properties: {\n    _id: "string",\n    name: "string",\n    age: "int",\n    // Create relationships by pointing an Object field to another Class\n    dogs: "Dog[]"\n  }\n};\n\n// Open a Realm\nconst realm = new Realm({schema: [Dog, Person]});\n\n// persist dog to the realm with a simple write transaction\nrealm.write(() => {\n\n  // Create Realm objects and use them just like regular JS objects\n  const dog = realm.create("Dog", { name: "Rex", age: 1 });\n});\n',
            position: '1'
          }
        ]
      }, {
        name: 'C#',
        snippets: [
          {
            code: '// Define your models like regular C# classes\npublic class Dog : RealmObject\n{\n    public string Name { get; set; }\n    public int Age { get; set; }\n}\n\npublic class Person : RealmObject\n{\n    [PrimaryKey]\n    public string Id { get; set; }\n    public string Name { get; set; }\n    public int Age { get: set;}\n    // Create relationships by pointing an Object field to another Class\n    public IList&#60;Dog&#62; Dogs { get; }\n}\n\n// Open a Realm reference\nvar realm = Realm.GetInstance();\n\n// Update and persist objects with a thread-safe transaction\nrealm.Write(() =>\n{\n    realm.Add(new Dog { Name = "Rex", Age = 1 });\n});',
            position: '1'
          }
        ]
      }
    ]
  }, {
    content: (
      <>
        <h3>Live Objects:<br />Build Reactive Apps</h3>
        <p>Realm’s live objects mean data updated anywhere is automatically updated everywhere. If you need to freeze elements of your data for Reactive frameworks, you can do that, too.</p>
        <a href='https://docs.mongodb.com/realm/sdk'>Learn More  →</a>
      </>
    ),
    codeLanguages: [
      {
        name: 'Kotlin',
        snippets: [
          {
            code: '// Open a realm\nrealm = Realm.getDefaultInstance()\n\n// Create a dog in the realm.\nrealm.executeTransaction { realm ->\n            dog = realm.createObject(Dog::class.java)\n            dog.name = "Max"\n }\n\n// Set up the listener.\nlistener = RealmObjectChangeListener&#60;Dog&#62; { dog, changeSet ->\n        if (changeSet?.isDeleted()) {\n                // Dog was deleted\n                return@RealmObjectChangeListener&#60;Dog&#62\n            }\n        for (fieldName in changeSet?.getChangedFields()) {\n                // "Field \'$fieldName\' changed."\n            }\n}\n\n// Observe object notifications.\ndog.addChangeListener(listener)\n\n// Update the dog\'s name to see the effect.\nrealm.executeTransaction { realm ->\n         dog.name = "Wolfie" // -> "Field \'name\' was changed."\n        }\n}',
            position: '1'
          }
        ]
      }, {
        name: 'Swift',
        snippets: [
          {
            code: '// Open the default realm.\nlet realm = try! Realm()\n\nvar token: NotificationToken?\n\nlet dog = Dog()\ndog.name = "Max"\n\n// Create a dog in the realm.\ntry! realm.write {\n    realm.add(dog)\n}\n\n//  Set up the listener & Observe object notifications.\ntoken = dog.observe { change in\n    switch change {\n    case .change(let properties):\n        for property in properties {\n            print("Property \'(property.name)\' changed to \'(property.newValue!)\'");\n        }\n    case .error(let error):\n        print("An error occurred: (error)")\n    case .deleted:\n        print("The object was deleted.")\n    }\n}\n\n// Update the dog\'s name to see the effect.\ntry! realm.write {\n    dog.name = "Wolfie"\n}',
            position: '1'
          }
        ]
      }, {
        name: 'JavaScript',
        snippets: [
          {
            code: '// Open the default realm.\nconst realm = await Realm.open();\n\nlet dog;\n\n// Create an entry.\nrealm.write(() => {\n    dog = realm.create(\'Dog\', {name: \'Max\', age: 2})\n });\n\n// Setup the Listener & Observe object notifications.\ndog.addListener((obj, changes) => {\n  // obj === dog\n  console.log(`object is deleted? ${changes.deleted}`);\n  console.log(`${changes.changedProperties.length} properties have been changed:`);\n  changes.changedProperties.forEach((prop) => {\n    console.log(` ${prop}`);\n  });\n});\n\n// Later, update the dog to trigger the notification.\nrealm.write(() => {\n  dog.name = \'Wolfie\'\n});',
            position: '1'
          }
        ]
      }, {
        name: 'C#',
        snippets: [
          {
            code: '// Open a Realm reference\nvar realm = Realm.GetInstance();\n\n// Create an entry.\nrealm.Write(() =>\n{\n    realm.Add(new Dog { Name = "Max"});\n});\n\nvar dog = realm.All&#60;Dog&#62;()\n    .FirstOrDefault(p => p.Name == "Max");\n\n// Setup the Listener & Observe object notifications.\ndog.PropertyChanged += (s, e) =>\n{\n    Debug.WriteLine($"New name set for Dog: {eventArgs.PropertyName}");\n};\n\n// Later, update the dog to trigger the notification.\nrealm.Write(() =>\n{\n    dog.Name = "Wolfie";\n});',
            position: '1'
          }
        ]
      }
    ]
  }, {
    content: (
      <>
        <h3>Fully Encrypted</h3>
        <p>Data can be encrypted in-flight and at-rest, keeping even the most sensitive data secure.</p>
        <a href='https://docs.mongodb.com/realm/sdk'>Learn More  →</a>
      </>
    ),
    codeLanguages: [
      {
        name: 'Kotlin',
        snippets: [
          {
            code: '// Generate a random encryption key\nval key = ByteArray(64)\nSecureRandom().nextBytes(key)\n\n// Set the encryption key in the config and open the realm\nval config = RealmConfiguration.Builder()\n    .encryptionKey(key)\n    .build()\n\nval realm = Realm.getInstance(config)\n\n// Use the realm as normal\nval dog = realm.where&#60;Dog&#62;().equalTo("name", "Fido").findFirst()',
            position: '1'
          }
        ]
      }, {
        name: 'Swift',
        snippets: [
          {
            code: '// Generate a random encryption key\nvar key = Data(count: 64)\n_ = key.withUnsafeMutableBytes { bytes in\n    SecRandomCopyBytes(kSecRandomDefault, 64, bytes)\n}\n\n// Add the encryption key to the config and open the realm\nlet config = Realm.Configuration(encryptionKey: key)\nlet realm = try Realm(configuration: config)\n\n// Use the Realm as normal\nlet dogs = realm.objects(Dog.self).filter("name contains \'Fido\'")',
            position: '1'
          }
        ]
      }, {
        name: 'JavaScript',
        snippets: [
          {
            code: '// Generate a random 64 byte encryption key\nconst key = new Int8Array(64); // Populate with a secure key\n\n// Specify the encryption key when you open a realm\nconst realm = new Realm({ encryptionKey: key });\n\n// Use the realm as normal\nconst dogs = realm.objects("Dog").filtered("name == \'Fido\'");',
            position: '1'
          }
        ]
      }, {
        name: 'C#',
        snippets: [
          {
            code: 'var config = new RealmConfiguration();\n\n// Generate a random 64 byte encryption key\nconfig.EncryptionKey = new byte[64] // Populate with a secure key\n\n// Add the encryption key to the config and open the realm\nvar realm = await Realm.GetInstanceAsync(config);\n\n// Use the Realm as normal\nvar dog = realm.All&#60;Dog&#62;().Where(p => p.Name == "Fido");',
            position: '1'
          }
        ]
      }
    ]
  }
]

const bottomCards = [
  <>
    <p>"A colleague brought the Realm Database to my attention, and I immediately fell in love with it. The whole schema is code and it just fit with how we were thinking."</p>
    <small>
      <strong>Isaac Overacker</strong><br />Software Architect, CBT Nuggets
    </small>
  </>,
  <>
    <p>"I boosted my database of 200,000 rows search from 7 seconds to 0.5 seconds with Realm. Practically under 30 mins of using it 1st time."</p>
    <small>
      <strong>Vijaya P. Kandel</strong><br />iOS Engineer
    </small>
  </>,
  <>
    <p>"Very impressive! I’m a huge fan of innovative solutions — especially critical processes like data persistence."</p>
    <small>
      <strong>Kevin Hoctor</strong><br />Software Engineer, Apple
    </small>
  </>
]

const header = {
  title: 'Build better apps, faster.',
  content: (
    <>
      <p>
        Realm’s mobile database is an open source, developer-friendly alternative to CoreData and SQLite. Start in minutes, port your app in hours, and save yourself weeks of work.
      </p>
      <p>
        Available for Swift, Objective-C, Java, Kotlin, C#, and JavaScript.
      </p>
    </>),
  links: [
    {
      text: 'Get Started →',
      href: 'https://docs.mongodb.com/realm/get-started/introduction-mobile'
    },
    {
      text: 'Docs →',
      href: 'https://docs.mongodb.com/realm/sdk'
    }
  ],
  image: 'https://webassets.mongodb.com/_com_assets/cms/home_background-fndrvy4xg5.svg'
}

const IndexPage = () => (
  <Layout header={header}>
    <SEO title='Home' description='Build better apps, faster'></SEO>
    <ContentBlock>
      <h2 style={{marginBottom: '40px'}}>Why Use Realm</h2>
      <CardGrid cards={topCards} cardWidth='360' checked />
    </ContentBlock>
    <ContentBlock backgroundColor='rgba(241, 240, 252, 0.35)'>
      {slaloms.map((slalom, i) => {
        return <Slalom content={slalom.content} codeLanguages={slalom.codeLanguages} key={i} />
      })}
      <CardGrid cards={middleCards} border background='white' cardWidth='560'/>
    </ContentBlock>
    <ContentBlock>
      <h2>Developers Love Realm</h2>
      <p>100k+ developers have built their apps on Realm, with more than 50k+ stars on GitHub. </p>
      <CardGrid cards={bottomCards} border background='rgba(241, 240, 252, 0.35)' cardWidth='360' />
    </ContentBlock>
  </Layout>
)

export default IndexPage
